/* eslint-disable @nx/enforce-module-boundaries */
import {
    APP_BASE_HREF,
    CommonModule,
    Location,
    PlatformLocation,
    registerLocaleData,
} from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi,
} from "@angular/common/http";
import localeEn from "@angular/common/locales/en";
import localeEnExtra from "@angular/common/locales/extra/en";
import localeFr from "@angular/common/locales/fr";
import localeJa from "@angular/common/locales/ja";
import localeZh from "@angular/common/locales/zh";
import {
    APP_ID,
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    LOCALE_ID,
    NgZone,
    provideAppInitializer,
} from "@angular/core";
import {
    provideClientHydration,
    withIncrementalHydration,
} from "@angular/platform-browser";
import {
    provideRouter,
    ROUTES,
    UrlSerializer,
    withEnabledBlockingInitialNavigation,
} from "@angular/router";

import { EffectsModule } from "@ngrx/effects";
import { provideRouterStore } from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateService } from "@ngx-translate/core";

import { CookieService } from "ngx-cookie-service";

import { LayoutStateModule } from "@hermes/aphrodite-layout-state";

import { CurrencyService } from "@hermes/aphrodite-price";
import {
    svgArrowDown,
    svgArrowLeft,
    svgArrowRight,
    svgBackCurvedArrow,
    svgCart,
    svgCross,
    svgExclamation,
    svgInfo,
    svgMinus,
    svgPlus,
    SvgRegistryService,
    svgRoundBarGrey,
} from "@hermes/aphrodite-svg";
import {
    AppCoreModule,
    Context,
    COUNTRY,
    ENV_CONFIG,
    INFRA_MAP,
    INFRA_SETTINGS,
    infraSettingsFactory,
    LANG,
    LOCALE,
    Settings,
    WINDOW_PROVIDERS,
} from "@hermes/core-app-core";
import { EnvironmentConfiguration } from "@hermes/core-env-infra";
import {
    ABTestInterceptor,
    CacheTagInterceptor,
    CreateUserSessionOn401UnauthorizedInterceptor,
    ForwardCookiesInterceptor,
    HComGatewayInterceptor,
    HttpErrorInterceptor,
    InterceptorService,
    LocaleInterceptor,
    RedirectOn404NotFoundInterceptor,
    RedirectOn410GoneInterceptor,
    WithCredentialsInterceptor,
    XsrfInterceptor,
} from "@hermes/core-interceptors";
import { RiskifiedBeaconServiceModule } from "@hermes/core-riskified-beacon-service";
import { RouterComponentsModule } from "@hermes/core-router-components";
import { SensorsDataServiceModule } from "@hermes/core-sensors-data-service";
import {
    CustomUrlSerializer,
    HermesErrorHandler,
} from "@hermes/core-web-custom";
import { LOGGER, LoggerService } from "@hermes/core-web-logger";
import { CartErrorInterceptorModule } from "@hermes/hermes-web-fragments-cart-error-interceptor";
import { ShellModule } from "@hermes/hermes-web-fragments-shell";
import { BasketStateModule } from "@hermes/hermes-web-states-basket-state";
import {
    FeatureFlagFacade,
    FlipperStateModule,
} from "@hermes/hermes-web-states-flipper-state";
import { RouterStateModule } from "@hermes/hermes-web-states-router-state";
import {
    TRAY_MODULES,
    TrayAnalyticsService,
    TrayFacade,
    TrayLoaderService,
    TrayStackService,
} from "@hermes/hermes-web-states-tray-state";
import { UserStateModule } from "@hermes/hermes-web-states-user-state";
import { trays } from "@hermes/hermes-web-trays-declarations";
import {
    countryFromCode,
    langFromCode,
    Locale,
    localeFromUrlPrefix,
    LocaleUrlPrefix,
} from "@hermes/locale";
import {
    HERMES_DEFAULT_INTERCEPTORS,
    HERMES_INTERCEPTORS,
} from "@hermes/utils-generic-constants";
import { ABTestingService } from "@hermes/utils-generic-services-ab-testing";
import { LoadExternalUrlStateModule } from "@hermes/utils-generic-services-load-external-url-service";

import { environment } from "../environments/environment";

import { APP_FUNCTIONAL_HTTP_INTERCEPTORS } from "./app-functional-http-interceptors";
import { APP_ROUTE_INTERCEPTORS } from "./app-http-route-interceptors";
import { initializeApplication } from "./app-initializer";
import { APP_ROUTES } from "./app.routes";
import { metaReducersFactory } from "./debug.reducer";
import { faroInitializer } from "./faro-initializer";

registerLocaleData(localeFr);
registerLocaleData(localeJa);
registerLocaleData(localeZh);
registerLocaleData(localeEn, "en");
registerLocaleData(localeEn, "gr", localeEnExtra);

// patch angular to NOT rewrite url without trailing slash
Location.stripTrailingSlash = (url: string): string => url;

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(APP_ROUTES, withEnabledBlockingInitialNavigation()),
        importProvidersFrom(
            BasketStateModule,
            CommonModule,
            AppCoreModule,
            FlipperStateModule,
            LayoutStateModule,
            RiskifiedBeaconServiceModule,
            SensorsDataServiceModule,
            RouterStateModule,
            ShellModule,
            RouterComponentsModule,
            UserStateModule,
            LoadExternalUrlStateModule.forRoot(),
            CartErrorInterceptorModule,
            EffectsModule.forRoot(),
            StoreModule.forRoot([], {
                runtimeChecks: {
                    strictStateImmutability: environment.ngrx.runtimeChecks,
                    strictActionImmutability: environment.ngrx.runtimeChecks,
                    // Serializability checks disabled as model-frontoffice is not serializable for now
                    strictStateSerializability: false,
                    strictActionSerializability: false,
                    strictActionWithinNgZone: environment.ngrx.runtimeChecks,
                    strictActionTypeUniqueness: environment.ngrx.runtimeChecks,
                },
                metaReducers: metaReducersFactory({
                    debug: environment.ngrx.debug,
                }),
            }),
            ...(environment.production
                ? []
                : [
                      StoreDevtoolsModule.instrument({
                          maxAge: 25,
                          logOnly: environment.production,
                          connectInZone: true,
                      }),
                  ]),
        ),
        provideHttpClient(
            withInterceptors(APP_FUNCTIONAL_HTTP_INTERCEPTORS),
            withInterceptorsFromDi(),
            withFetch(),
        ),
        provideClientHydration(withIncrementalHydration()),
        provideRouterStore(),
        { provide: APP_ID, useValue: "hermes" },
        ...WINDOW_PROVIDERS,
        ABTestingService,
        CookieService,
        CurrencyService,
        InterceptorService,
        TrayFacade,
        TrayLoaderService,
        TrayStackService,
        TrayAnalyticsService,
        {
            provide: INFRA_MAP,
            useFactory: (environmentConfig: EnvironmentConfiguration) =>
                environmentConfig.hosts,
            deps: [ENV_CONFIG],
        },
        {
            provide: INFRA_SETTINGS,
            useFactory: infraSettingsFactory,
            deps: [Context, INFRA_MAP],
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (platformLocation: PlatformLocation) =>
                platformLocation.getBaseHrefFromDOM().slice(0, -1),
            deps: [PlatformLocation],
        },
        {
            provide: LOCALE_ID,
            useFactory: (baseHref: LocaleUrlPrefix) =>
                localeFromUrlPrefix(baseHref).id,
            deps: [APP_BASE_HREF],
        },
        {
            provide: LOCALE,
            useFactory: (baseHref: LocaleUrlPrefix) =>
                localeFromUrlPrefix(baseHref),
            deps: [APP_BASE_HREF],
        },
        {
            provide: LANG,
            useFactory: (locale: Locale) => langFromCode(locale.langCode),
            deps: [LOCALE],
        },
        {
            provide: COUNTRY,
            useFactory: (locale: Locale) => countryFromCode(locale.countryCode),
            deps: [LOCALE],
        },
        { provide: HERMES_INTERCEPTORS, useValue: APP_ROUTE_INTERCEPTORS },
        {
            provide: HERMES_DEFAULT_INTERCEPTORS,
            useValue: [
                CreateUserSessionOn401UnauthorizedInterceptor,
                RedirectOn404NotFoundInterceptor,
            ],
        },
        provideAppInitializer(() => inject(InterceptorService).init()),
        provideAppInitializer(() =>
            initializeApplication(
                inject(FeatureFlagFacade),
                inject(TranslateService),
                inject(LOCALE),
            ),
        ),
        provideAppInitializer(() =>
            inject(SvgRegistryService).registerSvgs([
                svgPlus,
                svgMinus,
                svgArrowRight,
                svgArrowLeft,
                svgArrowDown,
                svgBackCurvedArrow,
                svgCart,
                svgRoundBarGrey,
                svgCross,
                svgExclamation,
                svgInfo,
            ]),
        ),
        provideAppInitializer(() =>
            faroInitializer(inject(Context), inject(Settings), inject(NgZone)),
        ),
        provideAppInitializer(() =>
            inject(ABTestingService).syncABTestToLocalStorage(),
        ),
        {
            provide: LOGGER,
            useClass: LoggerService,
        },
        {
            provide: ErrorHandler,
            useClass: HermesErrorHandler,
        },
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer,
        },
        // HTTP class-based interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: WithCredentialsInterceptor,
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LocaleInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheTagInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HComGatewayInterceptor,
            multi: true,
        },
        // A/B Test interceptor must be after HComGatewayInterceptor because when enabled, CF worker will will provide routes with `/api/*`
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ABTestInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ForwardCookiesInterceptor,
            multi: true,
        },
        // HTTP response error interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RedirectOn404NotFoundInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RedirectOn410GoneInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CreateUserSessionOn401UnauthorizedInterceptor,
            multi: true,
        },
        // reference to the lazy-loaded tray modules
        { provide: TRAY_MODULES, useValue: trays },
        { provide: ROUTES, useValue: trays, multi: true },
    ],
};
